/** Max file size the Chargeback analyze service will send to the LLM. */
export const CHARGEBACK_ANALYZE_MAX_BYTES = 5 * 1024 * 1024;

/** Always shown on the chargeback evidence upload screen. */
export const PAN_UPLOAD_WARNING =
  'Before you upload: Black out or remove every full card number (PAN) in your documents. Card network rules prohibit submitting unmasked card numbers, and files that contain one may be rejected. You are responsible for any fines or penalties that result from unredacted card numbers in the files you upload.';

/**
 * True only when the letter service scanned the text and found a Luhn-valid number.
 * Photos and scanned PDFs come back unscanned and are not treated as a confirmed hit.
 */
export function isConfirmedUnmaskedPan(analyze) {
  if (!analyze || analyze.contains_unmasked_pan !== true) return false;
  if (analyze.pan_detection_method !== 'deterministic') return false;
  const count = Number(analyze.pan_finding_count);
  return Number.isFinite(count) && count > 0;
}

const NON_PARSEABLE_EXTENSIONS = new Set([
  '.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v',
  '.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg',
  '.zip', '.rar', '.7z', '.tar', '.gz', '.tgz',
  '.exe', '.dll', '.bin', '.dmg', '.iso',
]);

const PARSEABLE_EXTENSIONS = new Set([
  '.pdf',
  '.doc', '.docx',
  '.txt', '.rtf',
  '.xls', '.xlsx', '.csv',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.tif', '.tiff', '.bmp',
]);

function fileExtension(fileName = '') {
  const name = String(fileName).toLowerCase();
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx) : '';
}

/**
 * Client-side parseability check mirroring the Chargeback analyze service rules.
 * Non-parseable files should require a manual note; analyze can be skipped.
 */
export function isChargebackFileParseable(file) {
  if (!file) return false;
  if (typeof file.size === 'number' && file.size > CHARGEBACK_ANALYZE_MAX_BYTES) {
    return false;
  }

  const ext = fileExtension(file.name);
  if (NON_PARSEABLE_EXTENSIONS.has(ext)) return false;
  if (PARSEABLE_EXTENSIONS.has(ext)) return true;

  const type = String(file.type || '').toLowerCase();
  if (
    type.startsWith('video/') ||
    type.startsWith('audio/') ||
    type.includes('zip') ||
    type.includes('compressed') ||
    type === 'application/octet-stream'
  ) {
    return false;
  }
  if (
    type.startsWith('image/') ||
    type.includes('pdf') ||
    type.startsWith('text/') ||
    type.includes('sheet') ||
    type.includes('excel') ||
    type.includes('word') ||
    type.includes('msword') ||
    type.includes('officedocument')
  ) {
    return true;
  }

  // Unknown types: let the service decide if called; treat as non-parseable for note UX.
  return false;
}

export function anySelectedFileNeedsManualNote(files = []) {
  return (files || []).some((file) => !isChargebackFileParseable(file));
}

/**
 * Analyze metadata is stored on DOCUMENT_UPLOADED rows in letter_html as JSON.
 */
export function parseUploadAnalyzeMeta(letterHtml) {
  if (!letterHtml || typeof letterHtml !== 'string') return null;
  try {
    const parsed = JSON.parse(letterHtml);
    if (parsed && typeof parsed === 'object' && parsed.analyze) {
      return parsed.analyze;
    }
    return null;
  } catch {
    return null;
  }
}

export function buildUploadAnalyzeLetterHtml(analyze = {}, extras = {}) {
  const findingCount = Number(analyze.pan_finding_count);
  return JSON.stringify({
    analyze: {
      parseable: Boolean(analyze.parseable),
      document_type: analyze.document_type || null,
      summary: analyze.summary || null,
      matched_suggestion_key:
        analyze.matched_suggestion_key != null
          ? String(analyze.matched_suggestion_key)
          : null,
      match_rationale: analyze.match_rationale || null,
      contains_unmasked_pan: analyze.contains_unmasked_pan === true,
      pan_detection_method: analyze.pan_detection_method || null,
      pan_finding_count: Number.isFinite(findingCount) ? findingCount : null,
    },
    ...extras,
  });
}

export function uploadAcceptedPanRisk(letterHtml) {
  if (!letterHtml || typeof letterHtml !== 'string') return false;
  try {
    const parsed = JSON.parse(letterHtml);
    return Boolean(parsed && parsed.pan_risk_accepted);
  } catch {
    return false;
  }
}

export function parseSuggestedDocumentsSnapshot(letterHtml) {
  if (!letterHtml || typeof letterHtml !== 'string') return null;
  try {
    const parsed = JSON.parse(letterHtml);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // Snapshot is a plain key->label map (no "analyze" wrapper).
      if (parsed.analyze) return null;
      const entries = Object.entries(parsed).filter(
        ([, value]) => value != null && String(value).trim()
      );
      if (!entries.length) return null;
      return Object.fromEntries(
        entries.map(([key, value]) => [String(key), String(value).trim()])
      );
    }
    return null;
  } catch {
    return null;
  }
}

/** Suggestion keys fulfilled by any uploaded file's matched_suggestion_key / item_type. */
export function getFulfilledSuggestionKeys(history = []) {
  const fulfilled = new Set();
  for (const item of history || []) {
    const action = String(item?.action || '').toUpperCase();
    if (action !== 'DOCUMENT_UPLOADED' && action !== 'FILE_UPLOAD') continue;

    const analyze = item?.analyzeMeta || parseUploadAnalyzeMeta(item?.letterHtml || item?.LETTER_HTML);
    const key =
      (analyze?.matched_suggestion_key != null && String(analyze.matched_suggestion_key)) ||
      (item?.itemType != null && String(item.itemType).trim()) ||
      '';
    if (key && /^\d+$/.test(key)) {
      fulfilled.add(key);
    }
  }
  return fulfilled;
}
